import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import uniqBy from 'lodash.uniqby';

import { ListItemState } from '../../../../../components/ListWithContentLayout/types';
import { type IFeeDto } from '../../../../License/types/response';
import { FeeFields } from '../../../../License/types/fieldEnums';

import styles from './FeeComparisonFooter.module.scss';

interface IFeeComparisonFooter {
  feeStates: Record<string, ListItemState>;
  fees: IFeeDto[];
}

function FeeComparisonFooter({ feeStates, fees }: IFeeComparisonFooter) {
  const { t } = useTranslation();

  const filteredFees = useMemo(() => uniqBy(fees, FeeFields.billNumber).map((fee) => fee.id), [fees]);

  const addedFees = useMemo(
    () =>
      Object.entries(feeStates).filter(
        ([id, state]) => state === ListItemState.Added && filteredFees.includes(Number(id))
      ).length,
    [feeStates, filteredFees]
  );

  const editedFees = useMemo(
    () =>
      Object.entries(feeStates).filter(
        ([id, state]) => state === ListItemState.Changed && filteredFees.includes(Number(id))
      ).length,
    [feeStates, filteredFees]
  );

  if (!addedFees && !editedFees) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      {editedFees !== 0 && (
        <span className={styles.editedBills}>
          {editedFees === 1 ? t('1 bill was updated') : t('{{count}} bills were updated', { count: editedFees })}
        </span>
      )}
      {addedFees !== 0 && (
        <span className={styles.addedBills}>
          {addedFees === 1 ? t('1 new bill was added') : t('{{count}} new bills were added', { count: addedFees })}
        </span>
      )}
    </div>
  );
}

export default FeeComparisonFooter;
